import React, { useEffect, useMemo, useState } from 'react';
import { nanoid } from 'nanoid';
import { useSelector } from 'react-redux';
import { Box, Text } from 'edenred-ui';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  calculateComission,
  calculateSuggestedTransferAmount
} from '@epi-helpers/loadAccountEstimationHelper';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import { formatCurrencyDecimal } from '@epi-helpers/numeral';
import {
  EstimationDetailsProps,
  ICheckableBenefit
} from '@epi-models/components/EstimationDetails';

import { getMonthNameForLocale } from '../../helpers/dateHelper';
import EstimationDetailsRow from './EstimationDetailsRow';
import {
  CalculatorHeader,
  CalculatorSummary
} from './EstimationDetails.styles';
import { EstimationFeeDescription } from './EstimationFeeDescription';

const EstimationDetails = ({
  estimation,
  setSuggestedTranserAmount,
  navigateToRequestedLoadsPage
}: EstimationDetailsProps) => {
  const { sizeExtraSmall, sizeMedium, primaryBackgroundColor } = useTheme();
  const { t } = useTranslation();
  const language = useSelector(getApplicationLanguageId);

  const [benefitDetails, setBenefitDetails] = useState<ICheckableBenefit[]>(
    estimation.benefits.map(b => ({ ...b, checked: true, id: nanoid() }))
  );
  const toggleCheckbox = (id: string): void => {
    setBenefitDetails(previous => {
      return previous.map(b => {
        return b.id === id
          ? {
              ...b,
              checked: !b.checked
            }
          : b;
      });
    });
  };

  const totalComission = useMemo(
    () => calculateComission(benefitDetails, estimation),
    [benefitDetails, estimation]
  );

  const totalSuggestedTransfer = useMemo(
    () =>
      calculateSuggestedTransferAmount(
        benefitDetails,
        estimation.totalSuggestedTransfer
      ),
    [benefitDetails, estimation]
  );

  useEffect(() => {
    setSuggestedTranserAmount(
      totalSuggestedTransfer > 0 ? totalSuggestedTransfer : 0
    );
  }, [totalSuggestedTransfer]);

  const currentMonth = getMonthNameForLocale(language, 0);
  const nextMonth = getMonthNameForLocale(language, 1);

  const currentBenefit = benefitDetails.find(
    benefit => benefit.month === 'current'
  );
  const nextBenefit = benefitDetails.find(benefit => benefit.month === 'next');

  return (
    <Box mt={3} p={2} bgcolor={primaryBackgroundColor}>
      <Text style={{ fontWeight: 600 }}>
        {t('containers.deposit_money.estimation_disclaimer_header', {
          numberOfMonths: benefitDetails.length
        })}
      </Text>
      <Text paragraph small>
        {t('containers.deposit_money.estimation_disclaimer')}
      </Text>
      <Box>
        <CalculatorHeader>
          <Text small id="requested-loads-details">
            {t('containers.deposit_money.requested_loads.details')}
          </Text>
          <Text small id="requested-loads-benefit">
            {t('containers.deposit_money.requested_loads.benefit')}
          </Text>
        </CalculatorHeader>
        {currentBenefit && (
          <EstimationDetailsRow
            message={t(`containers.deposit_money.${currentBenefit.type}.text`)}
            amount={currentBenefit.value}
            toggable
            checked={currentBenefit.checked}
            setChecked={toggleCheckbox}
            id={currentBenefit.id}
            monthId={currentBenefit.month}
            month={currentMonth}
          />
        )}
        <EstimationDetailsRow
          message={t('containers.deposit_money.requested_loads.message', {
            noOfRequests: estimation.requestedLoads.numberOfRequests
          })}
          amount={estimation.requestedLoads.value}
          hideIfZero
          action={navigateToRequestedLoadsPage}
          id="requested-loads"
        />
        {nextBenefit && (
          <EstimationDetailsRow
            message={t(`containers.deposit_money.${nextBenefit.type}.text`)}
            amount={nextBenefit.value}
            toggable
            checked={nextBenefit.checked}
            setChecked={toggleCheckbox}
            id={nextBenefit.id}
            monthId={nextBenefit.month}
            month={nextMonth}
          />
        )}
        <EstimationDetailsRow
          message={<EstimationFeeDescription />}
          amount={totalComission}
          hideIfZero
          id="loading-fee"
        />
        <EstimationDetailsRow
          message={t('containers.deposit_money.current_balance_label')}
          amount={estimation.currentBalance}
          id="balance"
          mergedColumns
        />
        <CalculatorSummary>
          <Box>
            <Box fontWeight={600} fontSize={sizeMedium}>
              {t('containers.deposit_money.suggested_transfer_amount')}
            </Box>
            <Box fontSize={sizeExtraSmall} pt={1}>
              {t('containers.deposit_money.suggested_amount')}
            </Box>
          </Box>
          <Box fontWeight={600} justifyContent="center">
            {formatCurrencyDecimal(estimation.totalSuggestedTransfer)}
          </Box>
        </CalculatorSummary>
      </Box>
    </Box>
  );
};

export default EstimationDetails;
